import { useEffect, useRef } from "react";
import Echo from "./Echo";
import { formatMonthYear, convertToMinutes } from "../../Utils/utils";
import { unstable_batchedUpdates } from "react-dom";
import { useGlobalContext } from "../../Context/GlobalContext";
import logger from "../../Utils/logger";
import { clearTempBookingId } from "../../Utils/tabHoldSession";
// import { toast } from "react-toastify";



function EventListner({ setNewAppointmentSlots, onBreakCreated, onCloseSidebar, onScheduleUpdated }) {
  const isConnected = useRef(false);
 const {
  selectedOutlet, 
  localSessionId, 
  temporaryBookingId,
  setTemporaryBookingId,
  setTempBookingCreated,
  setHitTemporaryBookingApi,
  setIsBookingInfoUpdated,
  selectedBookingId,
  setHomeStaffList,
  setBookingsList,
  bookingsList,
} = useGlobalContext();

logger.log("socket selectedOutlet", selectedOutlet);
 const latestTempBookingIdRef = useRef(temporaryBookingId);
 useEffect(() => {
  latestTempBookingIdRef.current = temporaryBookingId;
 }, [temporaryBookingId]);
 const latestSelectedBookingIdRef = useRef(selectedBookingId);
 useEffect(() => {
  latestSelectedBookingIdRef.current = selectedBookingId;
 }, [selectedBookingId]);
 const onCloseSidebarRef = useRef(onCloseSidebar);
 useEffect(() => {
  onCloseSidebarRef.current = onCloseSidebar;
 }, [onCloseSidebar]);
 const latestLocalSessionIdRef = useRef(localSessionId);
 useEffect(() => {
  latestLocalSessionIdRef.current = localSessionId;
 }, [localSessionId]);
  const isBreaksConnected = useRef(false);
  const crossOutletChannelRef = useRef(null);
  const holdBookingChannelRef = useRef(null);
  const deleteHoldBookingChannelRef = useRef(null);
  const crossOutletSubscribedRef = useRef(false);
  const holdBookingSubscribedRef = useRef(false);
  const deleteHoldBookingSubscribedRef = useRef(false);
  const currentCrossOutletRef = useRef(null);
  const currentHoldBookingOutletRef = useRef(null);
  const currentDeleteHoldBookingOutletRef = useRef(null);
  const processedDeleteBookingIdsRef = useRef(new Set());

  const normalizeSocketTimeWithSeconds = (timeValue) => {
    if (!timeValue) return null;
    const [rawHour = "0", rawMinute = "0", rawSecond = "0"] = String(timeValue).split(":");
    const hour = String(rawHour || "0").padStart(2, "0");
    const minute = String(rawMinute || "0").padStart(2, "0");
    const second = String(rawSecond || "0").padStart(2, "0");
    return `${hour}:${minute}:${second}`;
  };

  const formatSocketDateToDDMMYYYY = (dateValue) => {
    if (!dateValue || typeof dateValue !== "string") return null;
    const trimmedDate = dateValue.trim().split(" ")[0];
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmedDate)) {
      return trimmedDate;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
      const [year, month, day] = trimmedDate.split("-");
      return `${day}-${month}-${year}`;
    }
    return null;
  };

  const buildSocketServiceAt = (dateValue, timeValue, fallbackValue = null) => {
    const normalizedDate = formatSocketDateToDDMMYYYY(dateValue);
    const normalizedTime = normalizeSocketTimeWithSeconds(timeValue);
    if (!normalizedDate || !normalizedTime) return fallbackValue;
    const [day, month, year] = normalizedDate.split("-");
    return `${year}-${month}-${day} ${normalizedTime}`;
  };

  const resolveSocketTimingMeta = (item, additionalData = {}) => {
    const crossDaySegments = Array.isArray(additionalData?.cross_day_booking_item)
      ? additionalData.cross_day_booking_item
      : [];
    const fallbackServiceAt = item?.service_at || null;
    const fallbackStartDate = additionalData?.start_date ?? item?.start_date ?? null;
    const fallbackEndDate = additionalData?.end_date ?? item?.end_date ?? fallbackStartDate;
    const fallbackStartTime =
      item?.service_at?.split(" ")?.[1] ||
      item?.start_time ||
      additionalData?.start_time ||
      null;
    const fallbackEndTime =
      item?.service_end?.split(" ")?.[1] ||
      item?.end_time ||
      additionalData?.end_time ||
      null;
    const serviceAtDate = formatSocketDateToDDMMYYYY(item?.service_at || "");

    let matchingSegment = null;
    if (crossDaySegments.length > 0) {
      matchingSegment =
        crossDaySegments.find((segment) => segment?.start_date === serviceAtDate) ||
        crossDaySegments.find((segment) => segment?.start_date === fallbackStartDate) ||
        crossDaySegments.find((segment) => segment?.start_date === fallbackEndDate) ||
        crossDaySegments[0];
    }

    const startTime = normalizeSocketTimeWithSeconds(
      matchingSegment?.start_time || fallbackStartTime
    );
    const endTime = normalizeSocketTimeWithSeconds(
      matchingSegment?.end_time || fallbackEndTime
    );
    const startDate = matchingSegment?.start_date || fallbackStartDate || null;
    const endDate = matchingSegment?.end_date || fallbackEndDate || startDate || null;

    const segmentDurationSum =
      crossDaySegments.length > 0
        ? crossDaySegments.reduce(
            (total, segment) => total + (Number(segment?.duration) || 0),
            0
          )
        : 0;
    const bookingLevelDuration = Number(additionalData?.duration) || Number(item?.duration);

    // Same pattern as Home "dual context" expansion: identical times on different dates.
    // Duration is one slot (e.g. 60), not the sum of duplicate segments (120).
    const isDualContextExpansionSegments =
      crossDaySegments.length >= 2 &&
      (() => {
        const firstStart = crossDaySegments[0]?.start_time ?? null;
        const firstEnd = crossDaySegments[0]?.end_time ?? null;
        const allSameTimes = crossDaySegments.every(
          (segment) =>
            (segment?.start_time ?? null) === firstStart &&
            (segment?.end_time ?? null) === firstEnd
        );
        if (!allSameTimes) return false;
        const startMinutes = convertToMinutes(firstStart);
        const endMinutes = convertToMinutes(firstEnd);
        if (
          Number.isNaN(startMinutes) ||
          Number.isNaN(endMinutes) ||
          startMinutes >= 6 * 60 ||
          endMinutes >= 6 * 60
        ) {
          return false;
        }
        const uniqueDates = new Set(
          crossDaySegments.map((s) => s?.start_date || null).filter(Boolean)
        );
        return uniqueDates.size >= 2;
      })();

    let duration = Number(matchingSegment?.duration);
    // Multi-segment cross-night (midnight split): matched segment is only one part (e.g. 45),
    // row should use full span (e.g. 60). Do NOT sum segments for dual-context duplicates.
    if (crossDaySegments.length >= 2 && segmentDurationSum > 0 && !isDualContextExpansionSegments) {
      if (Number.isFinite(bookingLevelDuration) && bookingLevelDuration > 0 && bookingLevelDuration === segmentDurationSum) {
        duration = bookingLevelDuration;
      } else {
        duration = segmentDurationSum;
      }
    }
    if (isDualContextExpansionSegments && Number.isFinite(bookingLevelDuration) && bookingLevelDuration > 0) {
      duration = bookingLevelDuration;
    }
    if ((!Number.isFinite(duration) || duration <= 0) && startTime && endTime) {
      const startMinutes = convertToMinutes(startTime);
      const endMinutes = convertToMinutes(endTime);
      if (!Number.isNaN(startMinutes) && !Number.isNaN(endMinutes)) {
        duration = endMinutes >= startMinutes
          ? endMinutes - startMinutes
          : endMinutes + (24 * 60) - startMinutes;
      }
    }
    if (
      (!Number.isFinite(duration) || duration <= 0) &&
      crossDaySegments.length > 0 &&
      !isDualContextExpansionSegments
    ) {
      duration = crossDaySegments.reduce((total, segment) => total + (Number(segment?.duration) || 0), 0);
    }
    if (!Number.isFinite(duration) || duration <= 0) {
      duration = Number(additionalData?.duration) || Number(item?.duration) || 30;
    }

    return {
      startTime,
      endTime,
      duration,
      serviceAt: buildSocketServiceAt(startDate, startTime, fallbackServiceAt),
      startDate,
      endDate,
    };
  };

  useEffect(() => {
    if (isConnected.current) return;
    try {
      const channel = Echo.channel("outlet");
      if (typeof channel.subscribed === "function") {
        channel.subscribed(() => {
          logger.log("socket Successfully subscribed to outlet channel");
          isConnected.current = true;
        });
      }
      if (typeof channel.error === "function") {
        channel.error(() => {
          isConnected.current = false;
        });
      }

      // FOR CREATE BOOKING 
      channel.listen(".booking.created", (event) => {
        if (!event?.booking) return;
        logger.log("socket newBooking event", event);

        try {
          // Check if this booking was created on the same system (same session_id)
          const socketSessionId = event.booking?.session_id;
          const socketBookingId = event.booking?.booking_id || (event.booking?.booking_item && event.booking.booking_item[0]?.booking_id);
          const currentSelectedBookingId = String(latestSelectedBookingIdRef.current || '');
          const currentLocalSessionId = latestLocalSessionIdRef.current;          
          // Skip if booking was created on this same system
          if (socketSessionId && socketSessionId === currentLocalSessionId) {
            logger.log("socket Booking created on same system, skipping socket add");
            return;
          }
          
          // Also skip if booking_id matches the currently selected booking (locally created)
          if (socketBookingId && currentSelectedBookingId && String(socketBookingId) === currentSelectedBookingId) {
            logger.log("socket Booking matches locally created booking, skipping socket add");
            return;
          }
          
          const newBooking = transformSocketForUI(event.booking);
          logger.log("socket newBooking", newBooking);
          unstable_batchedUpdates(() => {
              setBookingsList((prev) => {
								const filtered = prev.filter((booking) => {
								  const bookingId = Number(booking.id || booking.booking_id);
								  const bookingStatus = String(
									booking.status || booking.booking_status || ""
								  )
									.trim()
									.toLowerCase();
							  
								  return !(
									bookingId === Number(newBooking?.id || newBooking?.booking_id) &&
									bookingStatus === "holding"
								  );
								});
							  
								return [...filtered, newBooking];
							});
              // Check for overlapping draft slots and clear them if matched
              // setNewAppointmentSlots((prevSlots) => {
              //   if (!prevSlots || prevSlots.length === 0) return prevSlots;
              //   // Get all items from the new booking flatly
              //   const newBookingItems = [];
              //    if (newBooking?.booking_item) {
              //       Object.values(newBooking.booking_item).forEach(items => {
              //           if (Array.isArray(items)) {
              //               newBookingItems.push(...items);
              //           }
              //       });
              //    }
              //    if (newBookingItems.length === 0) return prevSlots;
              //    // Check if ANY draft slot is covered by ANY new booking item
              //    const isMatchingBooking = prevSlots.some(draftSlot => {
              //       // Draft slot time
              //       const draftStart = convertToMinutes(draftSlot.start_time);
              //       const draftDuration = Number(draftSlot.duration) || 15;
              //       const draftEnd = draftStart + draftDuration;      
              //       // Draft therapist
              //       const draftTherapistId = draftSlot.therapistId ? String(draftSlot.therapistId) : null;
              //       const draftRoomId = draftSlot.roomId ? String(draftSlot.roomId) : null;
              //       return newBookingItems.some(bookedItem => {
              //           // Check resource match (Therapist OR Room)
              //           const bookedTherapistId = bookedItem.therapist_id ? String(bookedItem.therapist_id) : null;
              //           const bookedRoomId = bookedItem.room_id ? String(bookedItem.room_id) : null;
              //           const isResourceMatch = (draftTherapistId && draftTherapistId === bookedTherapistId) || 
              //                                 (draftRoomId && draftRoomId === bookedRoomId);
              //           if (!isResourceMatch) return false;
              //           // Check time overlap
              //           const bookedStart = convertToMinutes(bookedItem.start_time);
              //           const bookedEnd = convertToMinutes(bookedItem.end_time);
              //           if (bookedStart === null || bookedEnd === null) return false;
              //           // Simple overlap
              //           return (draftStart < bookedEnd && draftEnd > bookedStart);
              //       });
              //    });
              //    if (isMatchingBooking) {
              //        console.log("socket Creating booking matched draft slots - clearing drafts.");
              //        return [];
              //    }
              //    return prevSlots;
              // });
          });

        } catch (error) {
          logger.error("Error processing new booking", error);
        }
      });

      // FOR UPDATE BOOKING 
      channel.listen(".booking.updated", (event) => {
        if (!event?.booking) return;
        logger.log("socket updatedBooking event", event);
        const eventBookingId = event?.booking?.booking_id || event?.booking_id;
        const sourceOriginatedFrom = event?.booking?.sourceOriginatedFrom || event?.sourceOriginatedFrom;

        const fromApp = event?.booking?.from || event?.from;
        const panel = event?.booking?.panel || event?.panel;


        logger.log("socket updatedBooking eventBookingId", eventBookingId);
        logger.log("socket updatedBooking sourceOriginatedFrom", sourceOriginatedFrom);
        

        try {
          const updatedBooking = transformSocketForUpdate(event.booking);
          logger.log("socket updatedBooking", updatedBooking);
          
          // Convert both to strings for reliable comparison
          const updatedBookingId = String(updatedBooking.booking_id || updatedBooking.id || '');
          const selectedBookingId = String(latestSelectedBookingIdRef.current || '');
          
          logger.log("socket Comparing booking IDs - Updated:", updatedBookingId, "Selected:", selectedBookingId);

          const incomingBookingId = String(eventBookingId || updatedBookingId || '');
          const incomingPanel = String(sourceOriginatedFrom || '').toLowerCase();
          if (
            selectedBookingId &&
            incomingBookingId &&
            selectedBookingId === incomingBookingId &&
            incomingPanel === "customer"
          ) {
            logger.log("socket updatedBooking from customer panel for selected booking, closing sidebar");
            if (onCloseSidebarRef.current) {
              onCloseSidebarRef.current();
            }
          }

          const incomingFromApp = String(fromApp || "").toLowerCase();
          const incomingCheckinPanel = String(panel || "").toLowerCase();
          if (
            selectedBookingId &&
            incomingBookingId &&
            selectedBookingId === incomingBookingId &&
            (incomingFromApp === "app" || incomingCheckinPanel === "checkin")
          ) {
            logger.log("socket updatedBooking from app or checkin for selected booking, refreshing booking info");
            setIsBookingInfoUpdated(true);
          }
          
           // Trigger bookings list refresh
           try {
            window.dispatchEvent(
              new CustomEvent("refreshBookingList", {
                detail: { booking: updatedBooking },
              })
            );
          } catch (err) {
            logger.error("Error dispatching refreshBookingList event", err);
          }

          setBookingsList((prev) => {
            // Remove the old booking
            const filtered = prev.filter(
              (b) => String(b.booking_id || b.id) !== String(updatedBooking.booking_id)
            );
      
            // Add updated booking at the end (or you can add at the start if you prefer)
            return [...filtered, updatedBooking];
          });          

        } catch (error) {
          logger.error("Error processing update booking", error);
        }
      });


      

      // Listen for booking deletion
      channel.listen(".booking.deleted", (event) => {
        logger.log("socket delete event", event);
        try {
          const bookingId =
            event?.booking_id ||
            event?.id ||
            event?.booking?.booking_id ||
            event?.booking?.id ||
            event?.data?.booking_id ||
            event?.data?.id ||
            null;

          const saleId = event?.sale_id || event?.booking?.sale_id || event?.data?.sale_id || null;

          if (!bookingId && !saleId) return;

          if (bookingId) {
            setBookingsList((prev) =>
              prev.filter(
                (b) => String(b.booking_id || b.id) !== String(bookingId)
              )
            );
          }

          try {
            if (bookingId) {
              window.dispatchEvent(
                new CustomEvent("silentBookingRefresh", {
                  detail: { booking_id: bookingId },
                })
              );
            }
            if (saleId) {
              window.dispatchEvent(
                new CustomEvent("saleDeleted", {
                  detail: { sale_id: saleId },
                })
              );
            }
          } catch {}
        } catch {}
      });

      // Listen for booking cancelled
      channel.listen(".booking.cancelled", (event) => {
        try {
          const bookingId =
            event?.booking_id ||
            event?.id ||
            event?.booking?.booking_id ||
            event?.booking?.id ||
            event?.data?.booking_id ||
            event?.data?.id ||
            null;

          if (!bookingId) return;

          setBookingsList((prev) =>
            prev.map((b) =>
              String(b.booking_id || b.id) === String(bookingId)
                ? { ...b, status: "cancelled", cancelled: true }
                : b
            )
          );
          try {
            window.dispatchEvent(
              new CustomEvent("silentBookingRefresh", {
                detail: { booking_id: bookingId },
              })
            );
          } catch {}
        } catch {}
      });
      
      // FOR SCHEDULE UPDATE
      channel.listen(".therapist.schedule.updated", (event) => {
        logger.log("socket therapist schedule updated event", event);
        if (
          String(event?.outlet_id) === String(selectedOutlet) &&
          typeof onScheduleUpdated === "function"
        ) {
          onScheduleUpdated(event);
        }
      });

      return () => {
        isConnected.current = false;
        try {
          if (typeof channel.stopListening === "function") {
            channel.stopListening(".booking.created");
            channel.stopListening(".booking.deleted");
            channel.stopListening(".booking.cancelled");
            channel.stopListening(".therapist.schedule.updated");
          }
        } catch {}
        try {
          if (typeof Echo.leave === "function") {
            Echo.leave("outlet");
          }
        } catch {}
      };
    } catch {
      isConnected.current = false;
    }
  }, [setBookingsList, setNewAppointmentSlots, setIsBookingInfoUpdated, onScheduleUpdated]);

  // BREAKS SOCKET LISTENER
  useEffect(() => {
    // Verify Echo is available
    if (!Echo || typeof Echo.channel !== "function") {
      return;
    }
    
    if (isBreaksConnected.current) {
      return;
    }
    
    try {
      // Leave any existing channel first
      try {
        Echo.leave("breaks");
      } catch (err) {
        // Ignore if channel doesn't exist
      }
      
      const breaksChannel = Echo.channel("breaks");
      
      if (typeof breaksChannel.subscribed === "function") {
        breaksChannel.subscribed(() => {
          isBreaksConnected.current = true;
        });
      } else {
        isBreaksConnected.current = true;
      }
      
      if (typeof breaksChannel.error === "function") {
        breaksChannel.error((error) => {
          logger.error("Error subscribing to breaks channel:", error);
          isBreaksConnected.current = false;
        });
      }

      // Generic handler for all break events
      const handleBreakEvent = (event) => {
        try {
          // Call the callback to refresh breaks (only for created/updated/deleted events)
          if (onBreakCreated && typeof onBreakCreated === "function") {
            const eventStatus = event?.status;
            if (eventStatus === 'created' || eventStatus === 'updated' || eventStatus === 'deleted') {
              onBreakCreated();
            }
          }
        } catch (error) {
          logger.error("Error processing break event:", error);
        }
      };

      // The actual event name from backend is .BreakChanged with status: 'created'
      breaksChannel.listen(".BreakChanged", (event) => {
        // Handle different break statuses (created, updated, deleted)
        if (event?.status === 'created' || event?.status === 'updated' || event?.status === 'deleted') {
          handleBreakEvent(event);
        }
      });
      
      // Also try other possible event names for backward compatibility
      breaksChannel.listen("BreakCreate", (event) => {
        handleBreakEvent(event);
      });
      
      breaksChannel.listen(".BreakCreate", (event) => {
        handleBreakEvent(event);
      });
      
      breaksChannel.listen("break.created", (event) => {
        handleBreakEvent(event);
      });
      
      breaksChannel.listen(".break.created", (event) => {
        handleBreakEvent(event);
      });
      
      // Mark as connected after listeners are registered
      isBreaksConnected.current = true;

      return () => {
        isBreaksConnected.current = false;
        try {
          if (typeof breaksChannel.stopListening === "function") {
            breaksChannel.stopListening(".BreakChanged");
            breaksChannel.stopListening("BreakCreate");
            breaksChannel.stopListening(".BreakCreate");
            breaksChannel.stopListening("break.created");
            breaksChannel.stopListening(".break.created");
          }
        } catch (error) {
          // Ignore cleanup errors
        }
        try {
          if (typeof Echo.leave === "function") {
            Echo.leave("breaks");
          }
        } catch (error) {
          // Ignore cleanup errors
        }
      };
    } catch (error) {
      logger.error("Error setting up breaks socket listener:", error);
      isBreaksConnected.current = false;
    }
  }, [onBreakCreated]);

  // THERAPIST CACHE SYNC LISTENER
  useEffect(() => {
    if (!selectedOutlet || typeof setHomeStaffList !== "function") return;

    const channelName = `cache-sync.outlet.${selectedOutlet}.therapist`;
    const channel = Echo.channel(channelName);

    const handleTherapistSync = (event) => {
      logger.log("socket therapist sync event", event);
      const context = event?.context || {};
      const action = context.action || event?.action;
      const resourceType = context.resource_type || event?.resource_type;
      const resourceId = context.resource_id || event?.resource_id;
      const outletId = context.outlet_id || event?.outlet_id;

      if (action !== "deleted") return;
      if (resourceType && resourceType !== "therapist") return;
      if (outletId && String(outletId) !== String(selectedOutlet)) return;
      if (!resourceId) return;

      setHomeStaffList((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.filter((staff) => {
          const staffId = staff?.id || staff?.user_id || staff?.therapist_id;
          return String(staffId) !== String(resourceId);
        });
      });
    };

    channel.listen(".data.updated", handleTherapistSync);

    return () => {
      try {
        channel.stopListening(".data.updated");
      } catch {}
      try {
        if (typeof Echo.leave === "function") {
          Echo.leave(channelName);
        }
      } catch {}
    };
  }, [selectedOutlet, setHomeStaffList]);

 
  // FOR CREATE BOOKING 
  const transformSocketForUI = (socketBooking) => {
  const bookingItems = socketBooking.booking_item || [];
  // const bookingId = bookingItems.length > 0 ? bookingItems[0].booking_id : null;
  const bookingId = bookingItems.length > 0 ? String(bookingItems[0].booking_id) : null;
  const bookingItemMap = {};
  let user = null;

  bookingItems.forEach((item, index) => {
    let additionalData = {};
    if (item.additional) {
      // Check if item.additional is already an object or a JSON string
      if (typeof item.additional === 'object' && item.additional !== null) {
        // Already an object, use it directly
        additionalData = item.additional;
      } else if (typeof item.additional === 'string') {
        // It's a string, try to parse it
        try {
          additionalData = JSON.parse(item.additional);
        } catch {
          additionalData = {};
        }
      }
    }

    let memberName =
      index === 0 ? socketBooking.customer_name : additionalData.customer_name;
    memberName = memberName?.trim();

    user = {
      id: socketBooking?.customer_id || socketBooking?.customer,
      name: socketBooking.customer_name,
      contact_number: socketBooking?.customer_contact_number,
      membership: socketBooking?.member,
      created_at: formatMonthYear(socketBooking?.created_at),
    };

    if (!bookingItemMap[memberName]) {
      bookingItemMap[memberName] = [];
    }

    // Calculate duration: if cross_day_booking_item exists, find the segment that matches service_at date
    let calculatedDuration = additionalData.duration || 30;
    if (additionalData?.cross_day_booking_item && Array.isArray(additionalData.cross_day_booking_item) && additionalData.cross_day_booking_item.length > 0 && item.service_at) {
      // Parse service_at date (format: "2025-11-20 23:00:00" -> "20-11-2025")
      try {
        const serviceAtDate = new Date(item.service_at);
        const day = String(serviceAtDate.getDate()).padStart(2, '0');
        const month = String(serviceAtDate.getMonth() + 1).padStart(2, '0');
        const year = serviceAtDate.getFullYear();
        const serviceAtDateStr = `${day}-${month}-${year}`;
        
        // Find the segment where start_date matches service_at date
        const matchingSegment = additionalData.cross_day_booking_item.find(
          (segment) => segment.start_date === serviceAtDateStr
        );
        
        if (matchingSegment && matchingSegment.duration) {
          calculatedDuration = Number(matchingSegment.duration) || calculatedDuration;
        } else {
          // If no match found, fall back to sum of all durations
          calculatedDuration = additionalData.cross_day_booking_item.reduce((acc, curr) => acc + (Number(curr.duration) || 0), 0);
        }
      } catch (error) {
        // If date parsing fails, fall back to sum of all durations
        calculatedDuration = additionalData.cross_day_booking_item.reduce((acc, curr) => acc + (Number(curr.duration) || 0), 0);
      }
    }

    // Calculate timeRange: if cross_day_booking_item exists, use first and last item times
    // Otherwise, extract time from service_at and service_end
    let startTime = null;
    let endTime = null;
    if (additionalData?.cross_day_booking_item && Array.isArray(additionalData.cross_day_booking_item) && additionalData.cross_day_booking_item.length > 0) {
      startTime = additionalData.cross_day_booking_item[0].start_time;
      endTime = additionalData.cross_day_booking_item[additionalData.cross_day_booking_item.length - 1].end_time;
    } else if (item.service_at && item.service_end) {
      // Extract time portion from datetime strings (format: "2025-11-24 21:00:00" -> "21:00:00")
      startTime = item.service_at.split(' ')[1] || item.service_at;
      endTime = item.service_end.split(' ')[1] || item.service_end;
    }


    const room_items = (item?.room_segments || item?.room_items || []).map((segment) => ({
      room_id: segment?.room_id,
      room_name: segment?.item_type || segment?.room_name,
      start_time: segment?.service_at || startTime,
      end_time: segment?.service_end || endTime,
      duration: segment?.duration,
      priority: 1,
      facilities: Array.isArray(segment?.facilities) ? segment?.facilities : [],
      room_type: segment?.room_type ?? null,
    }));

    bookingItemMap[memberName].push({
      id: item.booking_item_id,
      booking_id: item.booking_id,
      therapist_id: item?.therapist_id || item?.user_id,
      gender: additionalData.gender || 'female',
      customer_name: memberName,
      service_id: item.service_id,
      service: item.service,
      service_category_name: item?.service_category_name,
      quantity: Number(item.quantity) || 1,
      price: item.price,
      status: 1,
      room_items,
      room_id: room_items[0]?.room_id || item.room_id || null,
      room_name: room_items[0]?.room_name || item?.room_name || null,
      service_at: item.service_at,
      start_time: startTime,
      end_time: endTime,
      duration: calculatedDuration,
      totalDuration: calculatedDuration,
      requested_person: additionalData.requested_person,
      requested_room: additionalData.requested_room,
      service_request: additionalData.service_request,
      primary: additionalData.primary,
      cross_day_booking_item: additionalData?.cross_day_booking_item || null,
      is_calendar_expansion: additionalData?.is_calendar_expansion ?? item?.is_calendar_expansion ?? item?.calendar_expansion ?? 0,
      start_date: additionalData?.start_date ?? item?.start_date ?? null,
      end_date: additionalData?.end_date ?? item?.end_date ?? null,
      commission: additionalData.commission,
      user_type: "new",
      memberName,
      recurring_group_id: socketBooking?.recurring_group_id || null,
      is_recurring: socketBooking?.recurring_group_id ? true : false,
      guest_checkin_id: item?.guest_checkin_id || null,
    });
  });

    return {
      id: bookingId,
      booking_id: bookingId,
      status: socketBooking?.status,
      panel: socketBooking?.panel,
      booking_type_id: 1,
      created_at: bookingItems.length > 0 ? bookingItems[0].service_at : null,
      user,
      booking_item: bookingItemMap,
      otherSystem: 1,
      // customerName: socketBooking?.customer_name,
      customer_name: socketBooking?.customer_name,
      mobile_number: socketBooking?.customer_contact_number,
      recurring_group_id: socketBooking?.recurring_group_id || null,
      is_recurring: socketBooking?.recurring_group_id ? true : false,
      source: socketBooking?.source,
      note: socketBooking?.notes,
      web_notes: socketBooking?.web_notes,
    };
  };

  // FOR UPDATE BOOKING 
  const transformSocketForUpdate = (socketBooking) => {
  const bookingItems = socketBooking.booking_item || [];
  // const bookingId = bookingItems.length > 0 ? bookingItems[0].booking_id : null;
  const bookingId = bookingItems.length > 0 ? String(bookingItems[0].booking_id) : null;
  const bookingItemMap = {};
  let user = null;

  bookingItems.forEach((item, index) => {
    let additionalData = {};
    if (item.additional) {
      // Check if item.additional is already an object or a JSON string
      if (typeof item.additional === 'object' && item.additional !== null) {
        // Already an object, use it directly
        additionalData = item.additional;
      } else if (typeof item.additional === 'string') {
        // It's a string, try to parse it
        try {
          additionalData = JSON.parse(item.additional);
        } catch {
          additionalData = {};
        }
      }
    }

    logger.log("socket update socketBooking", socketBooking);
    logger.log("socket update bookingItems", bookingItems);
    

    let memberName =
  index === 0
    ? socketBooking.customer_name
    : additionalData.customer_name || socketBooking.customer_name;

    memberName = memberName ? memberName.trim() : "Unknown";

    user = {
      id: socketBooking?.customer_id || socketBooking?.customer,
      name: socketBooking.customer_name,
      contact_number: socketBooking?.customer_contact_number,
      membership: socketBooking?.member,
      created_at: formatMonthYear(socketBooking?.created_at),
    };

    if (!bookingItemMap[memberName]) {
      bookingItemMap[memberName] = [];
    }
    
    const timingMeta = resolveSocketTimingMeta(item, additionalData);
    logger.log("socket update timingMeta--->", item);
    // const calculatedDuration = timingMeta.duration;
    const calculatedDuration = socketBooking?.panel === "customer" ? additionalData.duration : timingMeta.duration;
    const startTime = timingMeta.startTime;
    const endTime = timingMeta.endTime;

    const room_items = (item?.room_segments || item?.room_items || []).map((segment) => ({
      room_id: segment?.room_id,
      room_name: segment?.item_type || segment?.room_name,
      start_time: segment?.service_at || startTime,
      end_time: segment?.service_end || endTime,
      duration: segment?.duration,
      priority: 1,
      facilities: Array.isArray(segment?.facilities) ? segment?.facilities : [],
      room_type: segment?.room_type ?? null,
    }));

    bookingItemMap[memberName].push({
      id: item.booking_item_id,
      booking_id: item.booking_id,
      therapist_id: item?.user_id || item?.therapist_id,
      gender: additionalData.gender || item?.additional?.gender,
      service_id: item.service_id,
      service: item.service,
      service_category_name: item?.service_category_name,
      quantity: Number(item.quantity) || 1,
      price: item.price,
      status: 1,
      service_at: timingMeta.serviceAt,
      start_time: startTime,
      end_time: endTime,
      duration: calculatedDuration,
      totalDuration: calculatedDuration,
      requested_person: additionalData.requested_person || item?.additional?.requested_person,
      requested_room: additionalData.requested_room || item?.additional?.requested_room,
      service_request: additionalData.service_request || item?.additional?.service_request,
      primary: additionalData.primary || item?.additional?.primary,
      cross_day_booking_item: additionalData?.cross_day_booking_item || null,
      is_calendar_expansion:additionalData?.is_calendar_expansion ??item?.is_calendar_expansion ?? item?.calendar_expansion ?? item?.additional?.is_calendar_expansion ??0,
      start_date: timingMeta.startDate ?? additionalData?.start_date ?? item?.start_date ?? item?.additional?.start_date ?? null,
      end_date: timingMeta.endDate ?? additionalData?.end_date ?? item?.end_date ?? item?.additional?.end_date ?? null,
      room_items,
      room_id: room_items[0]?.room_id || item.room_id || null,
      room_name: room_items[0]?.room_name || item?.room_name || null,
      commission: additionalData.commission || item?.additional?.commission,
      user_type: "new",
      memberName,
      recurring_group_id: socketBooking?.recurring_group_id || null,
      is_recurring: socketBooking?.recurring_group_id ? true : false,
      isVoided: socketBooking?.original_booking_id ? true : false,
      guest_checkin_id: item?.guest_checkin_id || null,
      guest_first_name: item?.guest_checkin_first_name ?? additionalData?.guest_first_name ?? null,
      guest_last_name: item?.guest_checkin_last_name ?? additionalData?.guest_last_name ?? null,
      below_12: item?.below_12 ?? additionalData?.below_12 ?? 0,
      checkin_required: item?.checkin_required ?? additionalData?.checkin_required,
      checkin_exempt_reason: item?.checkin_exempt_reason ?? additionalData?.checkin_exempt_reason,
      addons: item?.addons || [],
    });

  });
    return {
      id: bookingId,
      booking_id: bookingId,
      status: socketBooking?.status === 'Updated' ? 'Confirmed' : socketBooking?.status,
      panel: socketBooking?.panel,
      booking_type_id: 1,
      created_at: socketBooking?.created_at,
      user,
      booking_item: bookingItemMap,
      otherSystem: 1,
      // customerName: socketBooking?.customer_name,
      customer_name: socketBooking?.customer_name,
      mobile_number: socketBooking?.customer_contact_number,
      recurring_group_id: socketBooking?.recurring_group_id || null,
      is_recurring: socketBooking?.recurring_group_id ? true : false,
      source: socketBooking?.source,
      note: socketBooking?.notes,
      web_notes: socketBooking?.web_notes,
    };
  };





  // FOR CREATE TEMPORARY BOOKING EVENT
  useEffect(() => {
    if (!selectedOutlet) return;
    
    const channelName = `cross-outlet-feature.${selectedOutlet}`;
    
    // If already subscribed to the same outlet, don't re-subscribe
    if (currentCrossOutletRef.current === selectedOutlet && 
        crossOutletSubscribedRef.current && 
        crossOutletChannelRef.current) {
      return;
    }
    
    // Clean up previous channel subscription if it exists (different outlet or not subscribed)
    if (crossOutletChannelRef.current) {
      const previousChannelName = `cross-outlet-feature.${currentCrossOutletRef.current || selectedOutlet}`;
      try {
        if (typeof crossOutletChannelRef.current.stopListening === "function") {
          crossOutletChannelRef.current.stopListening(".cross.outlet.feature.triggered");
        }
      } catch {}
      try {
        if (typeof Echo.leave === "function") {
          Echo.leave(previousChannelName);
        }
      } catch {}
      crossOutletChannelRef.current = null;
    }
    
    // Update current outlet ref
    currentCrossOutletRef.current = selectedOutlet;
    crossOutletSubscribedRef.current = false;
    
    try {
      // Subscribe to private channel for cross-outlet features
      const channel = Echo.private(channelName);
      crossOutletChannelRef.current = channel;
      
      if (typeof channel.subscribed === "function") {
        channel.subscribed(() => {
          logger.log(`socket Successfully subscribed to cross-outlet-feature channel for outlet ${selectedOutlet}`);
          crossOutletSubscribedRef.current = true;
        });
      } else {
        crossOutletSubscribedRef.current = true;
      }

      if (typeof channel.error === "function") {
        channel.error((error) => {
          logger.error(`Error subscribing to cross-outlet-feature channel for outlet ${selectedOutlet}:`, error);
          crossOutletChannelRef.current = null;
          crossOutletSubscribedRef.current = false;
          currentCrossOutletRef.current = null;
        });
      }

      // Listen for cross outlet feature event
      channel.listen(".cross.outlet.feature.triggered", (event) => {
        logger.log("socket Cross outlet feature event received", event);
        
        if (!event?.payload) return;
        
        try {
          const newBooking = transformCrossOutletForUI(event.payload);
          if( newBooking.session_id && newBooking.session_id !== localSessionId){
            setBookingsList((prev) => {
              const exists = prev.some(
                (b) => b.booking_id === newBooking.booking_id
              );
              if (exists) return prev;
              return [...prev, newBooking];
            });
          }
        } catch (error) {
          logger.error("Error processing cross outlet booking", error);
        }
      });

      return () => {
        crossOutletSubscribedRef.current = false;
        currentCrossOutletRef.current = null;
        try {
          if (crossOutletChannelRef.current && typeof crossOutletChannelRef.current.stopListening === "function") {
            crossOutletChannelRef.current.stopListening(".cross.outlet.feature.triggered");
          }
        } catch {}
        try {
          if (typeof Echo.leave === "function") {
            Echo.leave(channelName);
          }
        } catch {}
        crossOutletChannelRef.current = null;
      };
    } catch (error) {
      logger.error("Failed to initialize cross-outlet feature channel:", error);
      crossOutletChannelRef.current = null;
      crossOutletSubscribedRef.current = false;
      currentCrossOutletRef.current = null;
    }
  }, [selectedOutlet, setBookingsList, localSessionId]);
  // FOR CREATE TEMPORARY BOOKING EVENT END

  // FOR UPDATE TEMPORARY BOOKING EVENT
  useEffect(() => {
    if (!selectedOutlet) return;
    
    const channelName = `hold-booking-updated.${selectedOutlet}`;
    
    // If already subscribed to the same outlet, don't re-subscribe
    if (currentHoldBookingOutletRef.current === selectedOutlet && 
        holdBookingSubscribedRef.current && 
        holdBookingChannelRef.current) {
      return;
    }
    
    // Clean up previous channel subscription if it exists (different outlet or not subscribed)
    if (holdBookingChannelRef.current) {
      const previousChannelName = `hold-booking-updated.${currentHoldBookingOutletRef.current || selectedOutlet}`;
      try {
        if (typeof holdBookingChannelRef.current.stopListening === "function") {
          holdBookingChannelRef.current.stopListening(".hold.booking.updated");
        }
      } catch {}
      try {
        if (typeof Echo.leave === "function") {
          Echo.leave(previousChannelName);
        }
      } catch {}
      holdBookingChannelRef.current = null;
    }
    
    // Update current outlet ref
    currentHoldBookingOutletRef.current = selectedOutlet;
    holdBookingSubscribedRef.current = false;
    
    try {
      const channel = Echo.private(channelName);
      holdBookingChannelRef.current = channel;
      
      if (typeof channel.subscribed === "function") {
        channel.subscribed(() => {
          logger.log(`socket Successfully subscribed to hold booking channel for outlet ${selectedOutlet}`);
          holdBookingSubscribedRef.current = true;
        });
      } else {
        holdBookingSubscribedRef.current = true;
      }
      
      if (typeof channel.error === "function") {
        channel.error((error) => {
          logger.error(`Error subscribing to hold-booking-updated channel for outlet ${selectedOutlet}:`, error);
          holdBookingChannelRef.current = null;
          holdBookingSubscribedRef.current = false;
          currentHoldBookingOutletRef.current = null;
        });
      }
      
      channel.listen(".hold.booking.updated", (event) => {
        logger.log("socket Hold booking updated event received", event);

        if (!event?.payload) return;

        try {
          const updatedBooking = transformHoldBookingUpdatedForUI(event.payload);
          logger.log("socket hold booking updated", updatedBooking);

          logger.log("socket updatedBooking.session_id", updatedBooking.session_id, localSessionId);
          
          // Check if this is the creator's system (same session_id)
          const isCreatorSystem = updatedBooking.session_id && updatedBooking.session_id === localSessionId;
          
          const updatedBookingId = String(updatedBooking.booking_id);
          const updatedBookingStatus = String(updatedBooking.status || "").trim().toLowerCase();
          const isMatchingHoldBooking = (b) => {
            const listBookingId = String(b.booking_id || b.id);
            const listBookingStatus = String(
              b.status || b.booking_status || b.bookingStatus || ""
            ).trim().toLowerCase();

            return listBookingId === updatedBookingId && listBookingStatus === updatedBookingStatus;
          };

          if (isCreatorSystem) {
            // On creator's system: Only reset the timer by updating lastUpdated timestamp
            // Don't update the full booking object to avoid conflicts
            setBookingsList((prev) => {
              return prev.map((b) => {
                if (isMatchingHoldBooking(b)) {
                  // Update only lastUpdated to trigger timer reset via key change
                  return {
                    ...b,
                    lastUpdated: Date.now(),
                    holdingCardTime: updatedBooking?.expires_at ? (updatedBooking?.expires_at - Date.now()) / 1000 : 300, // Ensure holdingCardTime is set
                  };
                }
                return b;
              });
            });
          } else {
            // On other systems: Update the full booking object
            setBookingsList((prev) => {
              // Remove the old booking only when id and status both match
              const filtered = prev.filter((b) => !isMatchingHoldBooking(b));
              // Add updated booking at the end
              return [...filtered, updatedBooking];
            });
          }
        } catch (error) {
          logger.error("Error processing hold booking updated", error);
        }
      }); 
      
      return () => {
        holdBookingSubscribedRef.current = false;
        currentHoldBookingOutletRef.current = null;
        try {
          if (holdBookingChannelRef.current && typeof holdBookingChannelRef.current.stopListening === "function") {
            holdBookingChannelRef.current.stopListening(".hold.booking.updated");
          }
        } catch {}
        try {
          if (typeof Echo.leave === "function") {
            Echo.leave(channelName);
          }
        } catch {}
        holdBookingChannelRef.current = null;
      };
    } catch (error) {
      logger.error("Failed to initialize hold booking update channel:", error);
      holdBookingChannelRef.current = null;
      holdBookingSubscribedRef.current = false;
      currentHoldBookingOutletRef.current = null;
    }
  }, [selectedOutlet, setBookingsList, localSessionId]);
  // FOR UPDATE TEMPORARY BOOKING EVENT END

  // FOR CROSS OUTLET FEATURE BOOKING
  const transformCrossOutletForUI = (payload) => {
    const bookingId = String(payload.booking_id);
    const bookingItemMap = {};
    
    // Parse items JSON string
    let items = [];
    try {
      items = JSON.parse(payload.items || "[]");
    } catch (error) {
      logger.error("Error parsing items JSON:", error);
      items = [];
    }

    // Extract customer_name from items (usually in the first item)
    const customerName = items.length > 0 && items[0]?.customer_name 
      ? items[0].customer_name.trim() 
      : "Customer";

    // Extract session_id from items (usually in the first item)
    const sessionId = items.length > 0 && items[0]?.session_id 
      ? items[0].session_id 
      : null;

    // Convert service_at from "06-11-2025 15:15" (DD-MM-YYYY HH:MM) to "2025-11-06 15:15:00" (YYYY-MM-DD HH:MM:SS)
    // This format is needed for new Date() parsing in Home.js
    const convertServiceAtFormat = (serviceAtStr) => {
      if (!serviceAtStr) return serviceAtStr;
      const parts = serviceAtStr.split(' ');
      if (parts.length >= 2) {
        const datePart = parts[0]; // "06-11-2025"
        const timePart = parts[1]; // "15:15"
        const dateParts = datePart.split('-');
        if (dateParts.length === 3) {
          const [dd, mm, yyyy] = dateParts;
          const [rawHour = "", rawMinute = "", rawSecond = ""] = String(timePart).split(":");
          const normalizedHour = String(rawHour || "0").padStart(2, "0");
          const normalizedMinute = String(rawMinute || "0").padStart(2, "0");
          const normalizedSecond = String(rawSecond || "0").padStart(2, "0");
          const timeWithSeconds = `${normalizedHour}:${normalizedMinute}:${normalizedSecond}`;
          return `${yyyy}-${mm}-${dd} ${timeWithSeconds}`;
        }
      }
      return serviceAtStr; // Return as-is if format doesn't match
    };

    const convertedServiceAt = convertServiceAtFormat(payload.service_at);

    // Create user object with customer_name from items
    const user = {
      id: payload.customer_id,
      name: customerName,
      contact_number: null,
      membership: null,
      created_at: null,
    };

    // Process items
    items.forEach((item, index) => {
      // Use customer_name from item, fallback to extracted customerName, then "Customer"
      const memberName = item?.customer_name?.trim() || customerName;
      
      // If customer name and member name are the same, use empty string as key (like screenshot)
      const key = (memberName === customerName) ? "" : memberName;
      
      if (!bookingItemMap[key]) {
        bookingItemMap[key] = [];
      }

      bookingItemMap[key].push({
        id: payload.booking_id,
        booking_id: payload.booking_id,
        therapist_id: item?.therapist || null,
        gender: null,
        service_id: item?.service || null,
        service: item?.service || null,
        service_name: item?.service || null, // Add service_name for processing
        quantity: Number(item.quantity) || 1,
        price: item.price || "0",
        status: 1,
        room_id: item?.room_segments?.[0]?.room_id || null,
        service_at: convertServiceAtFormat(
          item?.start_date && item?.start_time
            ? `${item.start_date} ${item.start_time}`
            : payload.service_at
        ),
        duration: Number(item?.duration) || 40,
        requested_person: null,
        service_request: item.service_request || "",
        primary: item.primary || 0,
        start_time: item?.start_time || null,
        end_time: item?.end_time || null,
        start_date: item?.start_date || null,
        end_date: item?.end_date || null,
        cross_day_booking_item: item?.cross_day_booking_item || null,
        is_calendar_expansion:
          item?.is_calendar_expansion ??
          item?.calendar_expansion ??
          0,
        room_items: (item?.room_segments || []).map((segment) => ({
          room_id: segment?.room_id,
          item_name: segment?.item_type || null,
        })),
        commission: null,
        user_type: "new",
        memberName,
      });
    });

    // Parse expires_at timestamp string to calculate remaining time
    let expiresAtTimestamp = null;
    let holdingCardTime = 300; // Default 5 minutes
    if (payload?.expires_at) {
      try {
        // Parse ISO 8601 timestamp string (e.g., "2025-11-13T10:13:09.5203192")
        const expiresAtDate = new Date(payload.expires_at);
        if (!isNaN(expiresAtDate.getTime())) {
          expiresAtTimestamp = expiresAtDate.getTime();
          const remainingSeconds = Math.max(0, Math.floor((expiresAtTimestamp - Date.now()) / 1000));
          holdingCardTime = remainingSeconds;
        }
      } catch (error) {
        logger.error("Error parsing expires_at:", error);
      }
    }

    return {
      id: Number(bookingId),
      booking_id: Number(bookingId),
      status: payload?.status ?? "Holding",
      panel: "outlet",
      booking_type_id: 1,
      created_at: convertedServiceAt, // Use converted format
      user_id: payload.customer_id,
      user,
      user_name: payload?.user_name,
      booking_item: bookingItemMap,
      otherSystem: 1,
      customerName: customerName,
      outlet_id: payload.outlet_id,
      session_id: sessionId,
      is_calendar_expansion: items.some((item) => {
        const flag = item?.is_calendar_expansion ?? item?.calendar_expansion;
        return flag === 1 || flag === "1" || flag === true || flag === "true";
      }) ? 1 : 0,
      expires_At: expiresAtTimestamp || payload?.expires_at, // Store expires_at timestamp
      holdingCardTime: holdingCardTime,
      lastUpdated: Date.now(), // Add timestamp to track updates and force timer reset
    };
  };

  // FOR HOLD BOOKING UPDATED EVENT
  const transformHoldBookingUpdatedForUI = (payload) => {
    const bookingId = String(payload.booking_id);
    const bookingItemMap = {};
    
    // Parse items JSON string
    let items = [];
    try {
      items = JSON.parse(payload.items || "[]");
    } catch (error) {
      logger.error("Error parsing items JSON:", error);
      items = [];
    }

    // Extract customer_name from items (usually in the first item)
    const customerName = items.length > 0 && items[0]?.customer_name 
      ? items[0].customer_name.trim() 
      : "Customer";

    // Extract session_id from items (usually in the first item)
    const sessionId = items.length > 0 && items[0]?.session_id 
      ? items[0].session_id 
      : null;

    // Convert service_at from "07-11-2025 14:30" (DD-MM-YYYY HH:MM) to "2025-11-07 14:30:00" (YYYY-MM-DD HH:MM:SS)
    // This format is needed for new Date() parsing in Home.js
    const convertServiceAtFormat = (serviceAtStr) => {
      if (!serviceAtStr) return serviceAtStr;
      const parts = serviceAtStr.split(' ');
      if (parts.length >= 2) {
        const datePart = parts[0]; // "07-11-2025"
        const timePart = parts[1]; // "14:30"
        const dateParts = datePart.split('-');
        if (dateParts.length === 3) {
          const [dd, mm, yyyy] = dateParts;
          const [rawHour = "", rawMinute = "", rawSecond = ""] = String(timePart).split(":");
          const normalizedHour = String(rawHour || "0").padStart(2, "0");
          const normalizedMinute = String(rawMinute || "0").padStart(2, "0");
          const normalizedSecond = String(rawSecond || "0").padStart(2, "0");
          const timeWithSeconds = `${normalizedHour}:${normalizedMinute}:${normalizedSecond}`;
          return `${yyyy}-${mm}-${dd} ${timeWithSeconds}`;
        }
      }
      return serviceAtStr; // Return as-is if format doesn't match
    };

    const convertedServiceAt = convertServiceAtFormat(payload.service_at);

    // Create user object with customer_name from items (similar to transformSocketForUI)
    const user = {
      id: payload.customer_id,
      name: customerName,
      contact_number: null,
      membership: null,
      created_at: null,
    };

    // Process items similar to transformSocketForUI
    items.forEach((item, index) => {
      // Use customer_name from item, similar to transformSocketForUI logic
      // For first item, use customerName, for others use item.customer_name
      let memberName = index === 0 
        ? customerName 
        : (item?.customer_name?.trim() || customerName);
      memberName = memberName?.trim();

      // Don't remove member name if customer name and member name are the same
      // Use memberName directly as key (like transformSocketForUI)
      if (!bookingItemMap[memberName]) {
        bookingItemMap[memberName] = [];
      }

      bookingItemMap[memberName].push({
        id: null,
        booking_id: payload.booking_id,
        therapist_id: item?.therapist || null,
        gender: null,
        service_id: item?.service || null,
        service: item?.service || null,
        service_name: item?.service || null, // Add service_name for processing
        quantity: Number(item.quantity) || 1,
        price: item.price || "0",
        status: 1,
        room_id: item?.room_segments?.[0]?.room_id || null,
        service_at: convertServiceAtFormat(`${item.start_date} ${item.start_time}`),
        duration: Number(item.duration) || 15,
        requested_person: item.requested_person || 0,
        service_request: item.service_request || "",
        primary: item.primary || 0,
        start_time: item?.start_time || null,
        end_time: item?.end_time || null,
        start_date: item?.start_date || null,
        end_date: item?.end_date || null,
        cross_day_booking_item: item?.cross_day_booking_item || null,
        is_calendar_expansion:
          item?.is_calendar_expansion ??
          item?.calendar_expansion ??
          0,
        room_items: (item?.room_segments || []).map((segment) => ({
          room_id: segment?.room_id,
          item_name: segment?.item_type || null,
        })),
        commission: item.commission || null,
        user_type: "new",
        memberName, // Include memberName in item (like transformSocketForUI)
      });
    });

    // Parse expires_at timestamp string to calculate remaining time
    let expiresAtTimestamp = null;
    let holdingCardTime = 300; // Default 5 minutes
    if (payload?.expires_at) {
      try {
        // Parse ISO 8601 timestamp string (e.g., "2025-11-13T10:13:09.5203192")
        const expiresAtDate = new Date(payload.expires_at);
        if (!isNaN(expiresAtDate.getTime())) {
          expiresAtTimestamp = expiresAtDate.getTime();
          const remainingSeconds = Math.max(0, Math.floor((expiresAtTimestamp - Date.now()) / 1000));
          holdingCardTime = remainingSeconds;
        }
      } catch (error) {
        logger.error("Error parsing expires_at:", error);
      }
    }

    return {
      id: bookingId,
      booking_id: bookingId,
      status: payload?.status ?? "Holding",
      panel: "outlet",
      booking_type_id: 1,
      created_at: convertedServiceAt,
      user,
      user_name: payload?.user_name,
      booking_item: bookingItemMap,
      otherSystem: 1,
      customerName: customerName,
      outlet_id: payload.outlet_id,
      session_id: sessionId,
      is_calendar_expansion: items.some((item) => {
        const flag = item?.is_calendar_expansion ?? item?.calendar_expansion;
        return flag === 1 || flag === "1" || flag === true || flag === "true";
      }) ? 1 : 0,
      expires_At: expiresAtTimestamp || payload?.expires_at, // Store expires_at timestamp
      holdingCardTime: holdingCardTime,
      lastUpdated: Date.now(), // Add timestamp to track updates and force timer reset
    };
  };


  // Store stable references to callbacks using refs
  const setBookingsListRef = useRef(setBookingsList);
  const setTemporaryBookingIdRef = useRef(setTemporaryBookingId);
  const setTempBookingCreatedRef = useRef(setTempBookingCreated);
  const setHitTemporaryBookingApiRef = useRef(setHitTemporaryBookingApi);

  // Update refs when callbacks change
  useEffect(() => {
    onCloseSidebarRef.current = onCloseSidebar;
    setBookingsListRef.current = setBookingsList;
    setTemporaryBookingIdRef.current = setTemporaryBookingId;
    setTempBookingCreatedRef.current = setTempBookingCreated;
    setHitTemporaryBookingApiRef.current = setHitTemporaryBookingApi;
  }, [onCloseSidebar, setBookingsList, setTemporaryBookingId, setTempBookingCreated, setHitTemporaryBookingApi]);

  // FOR DELETE HOLD BOOKING EVENT
  useEffect(() => {
    if (!selectedOutlet) return;
    
    const channelName = `delete-hold-booking.${selectedOutlet}`;
    
    // If already subscribed to the same outlet, don't re-subscribe
    if (currentDeleteHoldBookingOutletRef.current === selectedOutlet && 
        deleteHoldBookingSubscribedRef.current && 
        deleteHoldBookingChannelRef.current) {
      return;
    }
    
    // Clean up previous channel subscription if it exists (different outlet or not subscribed)
    if (deleteHoldBookingChannelRef.current) {
      const previousChannelName = `delete-hold-booking.${currentDeleteHoldBookingOutletRef.current || selectedOutlet}`;
      try {
        if (typeof deleteHoldBookingChannelRef.current.stopListening === "function") {
          deleteHoldBookingChannelRef.current.stopListening(".delete.hold.booking");
        }
      } catch {}
      try {
        if (typeof Echo.leave === "function") {
          Echo.leave(previousChannelName);
        }
      } catch {}
      deleteHoldBookingChannelRef.current = null;
    }
    
    // Update current outlet ref
    currentDeleteHoldBookingOutletRef.current = selectedOutlet;
    deleteHoldBookingSubscribedRef.current = false;
    
    try {
      const channel = Echo.private(channelName);
      deleteHoldBookingChannelRef.current = channel;
      
      if (typeof channel.subscribed === "function") {
        channel.subscribed(() => {
          logger.log(`socket Successfully subscribed to delete-hold-booking channel for outlet ${selectedOutlet}`);
          deleteHoldBookingSubscribedRef.current = true;
        });
      } else {
        deleteHoldBookingSubscribedRef.current = true;
      }
      
      if (typeof channel.error === "function") {
        channel.error((error) => {
          logger.error(`Error subscribing to delete-hold-booking channel for outlet ${selectedOutlet}:`, error);
          deleteHoldBookingChannelRef.current = null;
          deleteHoldBookingSubscribedRef.current = false;
          currentDeleteHoldBookingOutletRef.current = null;
        });
      }
      
      channel.listen(".delete.hold.booking", (event) => {
        try {
          // Extract booking_id from event payload
          // Backend sends: { payload: { ... }, message: "..." }
          logger.log("socket delete hold booking event", event);
          const payload = event?.payload?.payload || event?.payload || {};
          logger.log("socket delete hold booking payload", payload);
          const bookingId = payload?.booking_hold_id || payload?.booking_id || payload?.hold_id;
          const bookingStatus = payload?.status ?? "Holding";

          if (!bookingId) {
            logger.warn("Delete hold booking event received but no booking_id found", event);
            return;
          }

          const bookingIdStr = String(bookingId);
          const bookingStatusNormalized = String(bookingStatus || "Holding").trim().toLowerCase();

          logger.log("socket delete hold booking id and status", bookingIdStr, bookingStatusNormalized);
          
          // Prevent duplicate processing - check if we've already processed this booking ID recently
          if (processedDeleteBookingIdsRef.current.has(bookingIdStr)) {
            logger.warn(`Delete hold booking event for booking ${bookingIdStr} already processed, skipping duplicate`);
            return;
          }

          // Mark as processed
          processedDeleteBookingIdsRef.current.add(bookingIdStr);
          
          // Clear from processed set after a short delay to allow legitimate re-processing if needed
          setTimeout(() => {
            processedDeleteBookingIdsRef.current.delete(bookingIdStr);
          }, 1000);

          // Remove only when both booking id and status match the event payload
          setBookingsListRef.current((prev) =>
            prev.filter(
              (b) => {
                const listBookingId = String(b?.booking_id || b?.id);
                const listBookingStatus = String(
                  b?.status || b?.booking_status || b?.bookingStatus || ""
                ).trim().toLowerCase();

                const isIdMatch = listBookingId === bookingIdStr;
                const isStatusMatch = listBookingStatus === bookingStatusNormalized;

                return !(isIdMatch && isStatusMatch);
              }
            )
          );

          // Check if the booking is the temporary booking
          const currentTemporaryBookingId = latestTempBookingIdRef.current;
          if(currentTemporaryBookingId !== null && bookingIdStr === String(currentTemporaryBookingId)) {
            // Call handleCloseSidebar to properly close the sidebar and reset all state
            clearTempBookingId();
            setTemporaryBookingIdRef.current(null);
            setTempBookingCreatedRef.current(false);
            setHitTemporaryBookingApiRef.current(false);
            if (onCloseSidebarRef.current) {
              onCloseSidebarRef.current();
            }
          }

        } catch (error) {
          logger.error("Error processing delete hold booking event:", error);
        }
      });
      
      return () => {
        deleteHoldBookingSubscribedRef.current = false;
        currentDeleteHoldBookingOutletRef.current = null;
        try {
          if (deleteHoldBookingChannelRef.current && typeof deleteHoldBookingChannelRef.current.stopListening === "function") {
            deleteHoldBookingChannelRef.current.stopListening(".delete.hold.booking");
          }
        } catch {}
        try {
          if (typeof Echo.leave === "function") {
            Echo.leave(channelName);
          }
        } catch {}
        deleteHoldBookingChannelRef.current = null;
      };
    } catch (error) {
      logger.error("Failed to initialize delete-hold-booking channel:", error);
      deleteHoldBookingChannelRef.current = null;
      deleteHoldBookingSubscribedRef.current = false;
      currentDeleteHoldBookingOutletRef.current = null;
    }
  }, [selectedOutlet]);
  // FOR DELETE HOLD BOOKING EVENT END
  



  return null;
}

export default EventListner;
